<template>
  <div class="external-product-images-history-screen">
    <v-card flat class="external-product-images-history__filters mt-4">
    <v-card-title class="text-subtitle-2">Búsqueda avanzada</v-card-title>
    <v-card-text class="external-product-images-history__content">
      <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>
      <v-row dense align="center">
        <v-col cols="12" sm="6" md="4"><v-text-field v-model="filters.from" type="date" label="Desde" outlined dense /></v-col>
        <v-col cols="12" sm="6" md="4"><v-text-field v-model="filters.to" type="date" label="Hasta" outlined dense /></v-col>
        <v-col cols="12" sm="12" md="4"><v-text-field v-model="filters.search" label="Buscar producto o usuario" placeholder="Nombre, código o usuario" outlined dense clearable /></v-col>
        <v-col cols="12" sm="6" md="4"><v-text-field v-model="filters.productId" label="ID producto" placeholder="PRO..." outlined dense /></v-col>
        <v-col cols="12" sm="6" md="4"><v-text-field v-model="filters.userId" label="ID usuario" placeholder="USR..." outlined dense /></v-col>
        <v-col cols="12" sm="6" md="4"><v-select v-model="filters.s3Status" :items="s3Statuses" label="Estado S3" outlined dense clearable /></v-col>
        <v-col cols="12" sm="6" md="4"><v-select v-model="filters.associationStatus" :items="associationStatuses" label="Asociación" outlined dense clearable /></v-col>
      </v-row>
      <div class="external-product-images-history__filter-actions">
        <v-btn color="primary" :loading="loading" prepend-icon="mdi-filter" @click="applyFilters">Filtrar</v-btn>
        <v-btn color="warning" dark :disabled="loading" prepend-icon="mdi-close-box-outline" @click="clearFilters">Limpiar</v-btn>
      </div>
    </v-card-text>
  </v-card>

    <v-card flat class="external-product-images-history__results mt-4">
    <v-card-text class="external-product-images-history__content">
      <div class="external-product-images-history__toolbar">
        <v-btn icon small title="Exportar historial" :loading="exporting" :disabled="loading" @click="exportHistory">
          <v-icon>mdi-file-download-outline</v-icon>
        </v-btn>
        <v-btn color="error" outlined small :disabled="!selectedIds.length || loading" @click="deleteSelected">
          Eliminar seleccionados ({{ selectedIds.length }})
        </v-btn>
      </div>
      <DxDataGrid
        v-if="items.length"
        class="external-product-images-history__grid"
        :data-source="items"
        key-expr="ID_Image"
        :show-borders="true"
        :column-auto-width="true"
        :row-alternation-enabled="true"
        :selected-row-keys="selectedIds"
        @selection-changed="historySelectionChanged"
        @exporting="onHistoryGridExporting"
      >
        <DxSelection mode="multiple" show-check-boxes-mode="always" select-all-mode="page" />
        <DxExport :enabled="true" />
        <DxPaging :page-size="20" />
        <DxPager :show-page-size-selector="true" :allowed-page-sizes="[10, 20, 50, 100]" :show-info="true" />
        <DxColumn cell-template="history-actions" caption="Acciones" :width="88" :allow-exporting="false" :allow-sorting="false" />
        <template #history-actions="{ data }">
          <div class="external-product-images-history__row-actions">
            <v-btn icon small color="primary" title="Ver detalle" @click.stop="openDetails(data.data || data)"><v-icon small>mdi-information-outline</v-icon></v-btn>
            <v-btn icon small color="error" title="Eliminar localmente" @click.stop="deleteRecord(data.data || data)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
          </div>
        </template>
        <DxColumn caption="Fecha" :calculate-cell-value="historyDate" />
        <DxColumn data-field="ID_Item" caption="ID producto" />
        <DxColumn data-field="productName" caption="Producto" />
        <DxColumn data-field="originalFileName" caption="Archivo" />
        <DxColumn data-field="destinationKey" caption="Destino" />
        <DxColumn data-field="s3Status" caption="S3" />
        <DxColumn data-field="associationStatus" caption="Asociación" />
        <DxColumn data-field="userName" caption="Usuario" />
      </DxDataGrid>
      <v-alert v-else-if="!loading" type="info" outlined dense class="mt-4">No hay registros para los filtros seleccionados.</v-alert>
      <v-progress-linear v-if="loading" class="mt-4" indeterminate color="primary" />
    </v-card-text>
    </v-card>

    <v-card flat class="external-product-images-history__missing mt-4">
    <v-card-title class="external-product-images-history__missing-title">
      <span>Productos activos sin imágenes registradas</span>
      <span class="external-product-images-history__missing-actions">
        <v-btn v-if="missingProductsLoaded" color="success" outlined small :loading="missingProductsExporting" @click="exportProductsWithoutImages('xlsx')">
          Excel
        </v-btn>
        <v-btn v-if="missingProductsLoaded" color="secondary" outlined small :loading="missingProductsExporting" @click="exportProductsWithoutImages('csv')">
          CSV
        </v-btn>
        <v-btn color="primary" small :loading="missingProductsLoading" @click="loadProductsWithoutImages">
          Consultar reporte
        </v-btn>
      </span>
    </v-card-title>
    <v-card-text class="external-product-images-history__content">
      <v-alert v-if="missingProductsError" type="error" dense>{{ missingProductsError }}</v-alert>
      <v-alert v-if="!missingProductsLoaded && !missingProductsError" type="info" outlined dense>
        Presiona “Consultar reporte” para cargar los productos activos que no tienen imágenes registradas.
      </v-alert>
      <DxDataGrid
        v-if="missingProductsLoaded && missingProducts.length"
        class="external-product-images-history__grid"
        :data-source="missingProducts"
        key-expr="ID_Item"
        :show-borders="true"
        :column-auto-width="true"
        :row-alternation-enabled="true"
        @exporting="onMissingGridExporting"
      >
        <DxExport :enabled="true" />
        <DxPaging :page-size="20" />
        <DxPager :show-page-size-selector="true" :allowed-page-sizes="[10, 20, 50, 100]" :show-info="true" />
        <DxColumn data-field="ID_Item" caption="ID producto" />
        <DxColumn data-field="itemName" caption="Nombre" />
        <DxColumn data-field="commercialName" caption="Nombre comercial" />
        <DxColumn caption="Sucursal" :calculate-cell-value="missingBranch" />
      </DxDataGrid>
      <v-alert v-else-if="missingProductsLoaded && !missingProductsLoading && !missingProductsError" type="success" outlined dense>
        No hay productos activos sin imágenes registradas.
      </v-alert>
      <v-progress-linear v-if="missingProductsLoading" class="mt-4" indeterminate color="primary" />
    </v-card-text>
    </v-card>

    <v-dialog v-model="detailDialog" max-width="850">
      <v-card v-if="selectedRecord">
        <v-card-title>Detalle de imagen {{ selectedRecord.ID_Image }}</v-card-title>
        <v-card-text>
          <div><strong>Ruta original:</strong> {{ selectedRecord.relativeSourcePath }}</div>
          <div><strong>Destino:</strong> {{ selectedRecord.destinationBucket }}/{{ selectedRecord.destinationKey }}</div>
          <div><strong>Estado S3:</strong> {{ s3StatusLabel(selectedRecord.s3Status) }}</div>
          <div><strong>Asociación:</strong> {{ associationStatusLabel(selectedRecord.associationStatus) }}</div>
          <div><strong>Resultado S3:</strong> {{ detailValue(selectedRecord.s3Result) }}</div>
          <div><strong>Resultado asociación:</strong> {{ detailValue(selectedRecord.associationResult) }}</div>
          <div v-if="selectedRecord.isDeleted" class="error--text mt-2">Eliminada lógicamente. No se borró el objeto S3 ni se liberó el correlativo.</div>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="detailDialog = false">Cerrar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { DxDataGrid, DxColumn, DxSelection, DxExport, DxPaging, DxPager } from 'devextreme-vue/data-grid';
import { printToExcel } from '@/helpers/printToexcel';

export default {
  name: 'ManageExternalProductImagesHistory',
  components: { DxDataGrid, DxColumn, DxSelection, DxExport, DxPaging, DxPager },
  props: {
    canManageImages: { type: Boolean, default: false }
  },
  data() {
    return {
      items: [],
      loading: false,
      exporting: false,
      error: '',
      missingProducts: [],
      missingProductsLoaded: false,
      missingProductsLoading: false,
      missingProductsExporting: false,
      missingProductsError: '',
      missingProductsPagination: { page: 1, pageSize: 20, total: 0, pages: 1 },
      filters: { from: '', to: '', search: '', productId: '', userId: '', s3Status: '', associationStatus: '', page: 1, pageSize: 20 },
      pagination: { page: 1, pageSize: 20, total: 0, pages: 1 },
      selectedIds: [],
      s3Statuses: ['PENDING', 'UPLOADING', 'UPLOADED', 'FAILED', 'UNKNOWN'],
      associationStatuses: ['PENDING', 'ASSOCIATING', 'ASSOCIATED', 'FAILED', 'UNKNOWN'],
      selectedRecord: null,
      detailDialog: false
    };
  },
  created() {
    this.loadHistory();
  },
  methods: {
    errorMessage(error, fallback) {
      return error?.response?.data?.message || error?.message || fallback;
    },
    historyParams() {
      return Object.keys(this.filters).reduce((result, key) => {
        if (this.filters[key] !== '' && this.filters[key] !== null && this.filters[key] !== undefined) result[key] = this.filters[key];
        return result;
      }, {});
    },
    async loadHistory() {
      this.loading = true;
      this.error = '';
      try {
        const response = await this.$API.externalProductImages.getHistory({ ...this.historyParams(), all: true });
        this.items = response.data?.items || [];
        this.pagination = response.data?.pagination || this.pagination;
        const visibleIds = new Set(this.items.map((item) => Number(item.ID_Image)));
        this.selectedIds = this.selectedIds.filter((id) => visibleIds.has(id));
      } catch (error) {
        this.error = this.errorMessage(error, 'No se pudo consultar el historial');
      } finally {
        this.loading = false;
      }
    },
    async loadProductsWithoutImages() {
      this.missingProductsLoading = true;
      this.missingProductsError = '';
      this.missingProductsLoaded = true;
      try {
        const response = await this.$API.externalProductImages.getProductsWithoutImages({ all: true });
        this.missingProducts = response.data?.items || [];
        this.missingProductsPagination = response.data?.pagination || this.missingProductsPagination;
      } catch (error) {
        this.missingProductsError = this.errorMessage(error, 'No se pudo consultar el reporte de productos sin imágenes');
      } finally {
        this.missingProductsLoading = false;
      }
    },
    downloadRows(rows, fileName, format, sheetName) {
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.writeFile(workbook, `${fileName}.${format}`, { bookType: format, bookSST: false });
    },
    async exportProductsWithoutImages(format) {
      this.missingProductsExporting = true;
      try {
        this.downloadRows(this.missingProducts.map((product) => ({
          'ID producto': product.ID_Item,
          Nombre: product.itemName || '',
          'Nombre comercial': product.commercialName || '',
          Sucursal: product.isGlobalBranch ? 'Global' : (product.ID_Branch || '')
        })), `productos-sin-imagenes-${new Date().toISOString().slice(0, 10)}`, format, 'Productos sin imágenes');
        this.$toasted.success(`Reporte ${format.toUpperCase()} descargado`);
      } catch (error) {
        this.missingProductsError = this.errorMessage(error, 'No se pudo descargar el reporte');
      } finally {
        this.missingProductsExporting = false;
      }
    },
    applyFilters() {
      this.filters.page = 1;
      this.loadHistory();
    },
    historySelectionChanged(event) {
      this.selectedIds = event.selectedRowKeys || [];
    },
    missingBranch(product) {
      return product.isGlobalBranch ? 'Global' : (product.ID_Branch || '—');
    },
    historyDate(item) {
      return item.createdAt ? String(item.createdAt).slice(0, 10) : '—';
    },
    onHistoryGridExporting(event) {
      printToExcel(event, 'Historial de imágenes');
    },
    onMissingGridExporting(event) {
      printToExcel(event, 'Productos sin imágenes');
    },
    async exportHistory() {
      this.exporting = true;
      try {
        this.downloadRows(this.items.map((item) => ({
          'ID imagen': item.ID_Image,
          Fecha: item.createdAt,
          'ID producto': item.ID_Item,
          'Nombre producto': item.productName,
          'Archivo original': item.originalFileName,
          'Ruta de origen': item.relativeSourcePath,
          Bucket: item.destinationBucket,
          'Key destino': item.destinationKey,
          Orden: item.imageOrder,
          Correlativo: item.correlative,
          'Estado S3': item.s3Status,
          'Estado asociación': item.associationStatus,
          'ID usuario': item.userInsert,
          'Nombre usuario': item.userName,
          'ID batch': item.ID_Batch
        })), `historial-imagenes-${new Date().toISOString().slice(0, 10)}`, 'csv', 'Historial de imágenes');
        this.$toasted.success('Historial exportado');
      } catch (error) {
        this.error = this.errorMessage(error, 'No se pudo exportar el historial');
      } finally {
        this.exporting = false;
      }
    },
    clearFilters() {
      this.filters = { from: '', to: '', search: '', productId: '', userId: '', s3Status: '', associationStatus: '', page: 1, pageSize: 20 };
      this.selectedIds = [];
      this.loadHistory();
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : '—';
    },
    detailValue(value) {
      return value ? JSON.stringify(value) : '—';
    },
    s3StatusLabel(status) {
      return { PENDING: 'Pendiente', UPLOADING: 'Cargando', UPLOADED: 'Confirmada', FAILED: 'Fallida', UNKNOWN: 'Incierta' }[status] || status || '—';
    },
    associationStatusLabel(status) {
      return { PENDING: 'Pendiente', ASSOCIATING: 'Procesando', ASSOCIATED: 'Asociada', FAILED: 'Fallida', UNKNOWN: 'Incierta' }[status] || status || '—';
    },
    openDetails(item) {
      this.selectedRecord = item;
      this.detailDialog = true;
    },
    async deleteRecord(item) {
      if (!window.confirm('Se eliminará solo el registro local. No se borrará S3 ni se liberará el correlativo. ¿Continuar?')) return;
      this.loading = true;
      try {
        await this.$API.externalProductImages.deleteHistoryRecord(item.ID_Image);
        this.$toasted.success('Registro eliminado localmente');
        await this.loadHistory();
      } catch (error) {
        this.error = this.errorMessage(error, 'No se pudo eliminar el registro');
      } finally {
        this.loading = false;
      }
    },
    async deleteSelected() {
      if (!this.selectedIds.length) return;
      if (!window.confirm(`Se eliminarán localmente ${this.selectedIds.length} registro(s). No se borrará S3 ni se liberarán correlativos. ¿Continuar?`)) return;
      this.loading = true;
      try {
        for (const imageId of this.selectedIds) {
          await this.$API.externalProductImages.deleteHistoryRecord(imageId);
        }
        this.selectedIds = [];
        this.$toasted.success('Registros eliminados localmente');
        await this.loadHistory();
      } catch (error) {
        this.error = this.errorMessage(error, 'No se pudieron eliminar todos los registros seleccionados');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.external-product-images-history-screen {
  box-sizing: border-box;
  width: 100%;
  min-height: 100%;
  padding: 12px 16px 24px;
  background-color: #ffffff !important;
}
.external-product-images-history__filters,
.external-product-images-history__results,
.external-product-images-history__missing {
  background-color: #ffffff !important;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.14) !important;
}
.external-product-images-history__content { padding: 4px 16px 14px !important; }
.external-product-images-history__missing-title { align-items: center; justify-content: space-between; gap: 12px; }
.external-product-images-history__missing-actions { display: inline-flex; align-items: center; gap: 8px; }
.external-product-images-history__filter-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 2px; }
.external-product-images-history__toolbar { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 8px; min-height: 28px; }
.external-product-images-history__row-actions { display: flex; align-items: center; justify-content: flex-start; gap: 0; min-width: 76px; }
.external-product-images-history__pagination { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 16px; }
.text-no-wrap { white-space: nowrap; }
@media (max-width: 900px) {
  .text-no-wrap { white-space: normal; }
  .external-product-images-history__toolbar { justify-content: flex-start; }
  .external-product-images-history__pagination { align-items: flex-start; flex-direction: column; }
}
</style>
